import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button, Input, Alert } from '@/components/ui';
import { api } from '@/lib/api';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // Redirect if no token
  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Invalid reset link');
      return;
    }

    setIsLoading(true);

    try {
      await api.resetPassword(token, formData.password);
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="verdict-card rounded-2xl p-8 sm:p-10 text-center max-w-md w-full mx-auto">
        <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">Password Reset!</h2>
        <p className="text-text-secondary mb-6">
          Your password has been successfully reset.
          <br />
          You can now log in with your new password.
        </p>
        <Link to="/login">
          <Button variant="primary" size="lg" className="w-full">
            Go to Login
          </Button>
        </Link>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="verdict-card rounded-2xl p-8 sm:p-10 text-center max-w-md w-full mx-auto">
        <div className="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-error" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">Invalid Reset Link</h2>
        <p className="text-text-secondary mb-6">
          This password reset link is invalid or has expired.
          <br />
          Please request a new password reset.
        </p>
        <Link to="/forgot-password">
          <Button variant="primary" size="lg" className="w-full">
            Request New Reset Link
          </Button>
        </Link>
        <p className="text-center text-text-muted mt-4">
          <Link to="/login" className="text-verdict-primary hover:text-verdict-primary-hover transition-colors inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="verdict-card rounded-2xl p-8 sm:p-10 max-w-md w-full mx-auto">
      <h2 className="text-2xl font-bold text-text-primary text-center mb-2">Reset Password</h2>
      <p className="text-text-secondary text-center mb-6">
        Enter your new password below
      </p>

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          name="password"
          type="password"
          label="New Password"
          placeholder="••••••••"
          value={formData.password}
          onChange={handleChange}
          leftIcon={<Lock className="w-5 h-5" />}
          helperText="Must be at least 8 characters"
          required
        />

        <Input
          name="confirmPassword"
          type="password"
          label="Confirm Password"
          placeholder="••••••••"
          value={formData.confirmPassword}
          onChange={handleChange}
          leftIcon={<Lock className="w-5 h-5" />}
          required
        />

        <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
          Reset Password
        </Button>
      </form>

      <p className="text-center text-text-muted mt-6">
        <Link to="/login" className="text-verdict-primary hover:text-verdict-primary-hover transition-colors inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </p>
    </div>
  );
}
